/**
 * McpKeyWorklist — admin "needs attention" worklist of MCP key-holders whose
 * live Entra status is not active (disabled / deleted / unknown), with a
 * per-key manual revoke.
 *
 * Self-contained state (holders, loading, error, pending revoke) analogous to
 * McpKeysSection. Reuses the ui/table primitives, Radix AlertDialog, and the
 * existing Alert/Button styling. Status is conveyed by TEXT label (not color
 * alone) per Section 508 / WCAG.
 */

import React, { useCallback, useEffect, useState } from 'react';
import type { AdminMcpKeyHolder, EntraAccountStatus } from '@coreflow/shared';
import { listMcpKeyHolders, adminRevokeMcpKey } from '../../api/admin-mcp-keys';
import { isClientError } from '../../services/api-client';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Entra statuses that place a holder on the primary needs-attention worklist. */
const NEEDS_ATTENTION_STATUSES: ReadonlySet<EntraAccountStatus> = new Set([
  'disabled',
  'deleted',
  'unknown',
]);

const UNKNOWN_SUBTEXT = 'Could not verify this account in your identity provider.';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Identifies the key targeted by the confirm dialog / in-flight revoke. */
interface PendingRevoke {
  readonly userId: string;
  readonly keyId: string;
  readonly keyName: string;
  readonly email: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractErrorMessage(error: unknown): string {
  if (isClientError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

/** Keeps only holders that need attention (non-active). */
function filterNeedsAttention(holders: AdminMcpKeyHolder[]): AdminMcpKeyHolder[] {
  return holders.filter((h) => NEEDS_ATTENTION_STATUSES.has(h.entraStatus));
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  readonly status: EntraAccountStatus;
}

/**
 * Renders the Entra status as a TEXT label (never color alone). 'Unknown'
 * carries distinguishing subtext so an operator does not misread it as safe.
 */
function StatusBadge({ status }: StatusBadgeProps): React.ReactElement {
  if (status === 'unknown') {
    return (
      <span className="text-muted-foreground">
        <span className="font-medium">Unknown</span>
        <span className="block text-xs">{UNKNOWN_SUBTEXT}</span>
      </span>
    );
  }

  const label = status === 'deleted' ? 'Deleted' : 'Disabled';
  return <span className="font-medium text-destructive">{label}</span>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function McpKeyWorklist(): React.ReactElement {
  const [holders, setHolders] = useState<AdminMcpKeyHolder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingRevoke | null>(null);
  const [revoking, setRevoking] = useState<boolean>(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await listMcpKeyHolders();
      setHolders(filterNeedsAttention(result.data.users));
    } catch (err: unknown) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openConfirm = useCallback((holder: AdminMcpKeyHolder, keyId: string, keyName: string): void => {
    setActionError(null);
    setPending({ userId: holder.userId, keyId, keyName, email: holder.email });
  }, []);

  const closeConfirm = useCallback((): void => {
    if (!revoking) setPending(null);
  }, [revoking]);

  const confirmRevoke = useCallback(async (): Promise<void> => {
    if (!pending) return;
    setRevoking(true);
    setActionError(null);
    try {
      await adminRevokeMcpKey(pending.userId, pending.keyId);
      // Remove the revoked key locally; drop the holder row if no keys remain.
      setHolders((prev) =>
        prev
          .map((h) =>
            h.userId === pending.userId
              ? { ...h, keys: h.keys.filter((k) => k.keyId !== pending.keyId) }
              : h,
          )
          .filter((h) => h.keys.length > 0),
      );
      setSuccessMessage(`Revoked key "${pending.keyName}" for ${pending.email}.`);
      setPending(null);
    } catch (err: unknown) {
      // Keep the row; surface the error and re-enable the action.
      setActionError(extractErrorMessage(err));
    } finally {
      setRevoking(false);
    }
  }, [pending]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center py-6" role="status" aria-label="Loading MCP key worklist">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
        <span className="ml-3 text-sm text-muted-foreground">Loading MCP key worklist…</span>
      </div>
    );
  }

  // Fetch failure
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
        <Button type="button" className="mt-2" onClick={() => void load()}>
          Retry
        </Button>
      </Alert>
    );
  }

  // Empty state
  if (holders.length === 0) {
    return (
      <Alert variant="info" role="status">
        <AlertDescription>No MCP keys need attention.</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      {successMessage && (
        <Alert variant="success" role="status" className="mb-4">
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>{successMessage}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSuccessMessage(null)}
              aria-label="Dismiss confirmation"
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {actionError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <Table aria-label="MCP key worklist">
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Display Name</TableHead>
            <TableHead scope="col">Email</TableHead>
            <TableHead scope="col">Entra Status</TableHead>
            <TableHead scope="col">Keys</TableHead>
            <TableHead scope="col"><span className="sr-only">Actions</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {holders.map((holder) => (
            <TableRow key={holder.userId}>
              <TableCell>{holder.displayName}</TableCell>
              <TableCell>{holder.email}</TableCell>
              <TableCell><StatusBadge status={holder.entraStatus} /></TableCell>
              <TableCell>
                <ul className="space-y-2">
                  {holder.keys.map((key) => (
                    <li key={key.keyId} className="text-sm">
                      <span className="font-medium">{key.name}</span>
                      {' '}
                      <code className="font-mono text-xs text-muted-foreground">{key.prefix}...</code>
                      {' '}
                      <span className="text-xs text-muted-foreground">
                        created {new Date(key.createdAt).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </TableCell>
              <TableCell>
                <ul className="space-y-2">
                  {holder.keys.map((key) => (
                    <li key={key.keyId}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openConfirm(holder, key.keyId, key.name)}
                        aria-label={`Revoke key ${key.name} for ${holder.email}`}
                      >
                        Revoke
                      </Button>
                    </li>
                  ))}
                </ul>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={pending !== null} onOpenChange={(open) => { if (!open) closeConfirm(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke key</AlertDialogTitle>
            <AlertDialogDescription>
              {pending
                ? `Revoke key "${pending.keyName}" for ${pending.email}? This immediately disables the key.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking} onClick={closeConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={revoking}
              onClick={(e) => { e.preventDefault(); void confirmRevoke(); }}
            >
              {revoking ? 'Revoking…' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
